import type { Era, Weighted, WeightedName } from '../types.js';

/**
 * German given names and surnames for synthetic BaseChatFun profiles.
 *
 * Given names follow the yearly German birth rankings (GfdS /
 * beliebte-vornamen.de) for the 1981–2008 cohorts the app's 18–45 users were
 * born in, weighted by how common each name is in that span. Turkish-German,
 * Arabic-, Balkan-, Polish- and Russian-German names are included at the
 * weights they carry in those cohorts. Surnames follow German frequency
 * (Müller, Schmidt, Schneider…) plus the commonest migrant surnames.
 *
 * Rows are `Name weight eras`; era letters: a=pre95 b=95-99 c=00-04 d=05-09.
 */

const ERA: Readonly<Record<string, Era>> = { a: 'pre95', b: '95-99', c: '00-04', d: '05-09', e: 'post09' };

function names(rows: string): WeightedName[] {
  return rows
    .split('\n')
    .flatMap((line) => line.split('|'))
    .map((row) => row.trim())
    .filter((row) => row !== '')
    .map((row) => {
      const parts = row.split(' ');
      const eraCodes = parts.pop() ?? '';
      const weight = Number(parts.pop());
      const eras = [...eraCodes].map((c) => {
        const era = ERA[c];
        if (era === undefined) throw new Error(`Unknown era code '${c}' in '${row}'`);
        return era;
      });
      return { name: parts.join(' '), weight, eras };
    });
}

function surnames(rows: string): Weighted[] {
  return rows
    .split('\n')
    .flatMap((line) => line.split('|'))
    .map((row) => row.trim())
    .filter((row) => row !== '')
    .map((row) => {
      const parts = row.split(' ');
      const weight = Number(parts.pop());
      return { name: parts.join(' '), weight };
    });
}

export const DE_FEMALE: WeightedName[] = names(`
  Julia 100 abc | Laura 95 abcd | Sarah 95 abcd | Anna 95 abcd | Lisa 90 abc | Lena 90 bcd | Lea 85 bcd
  Leonie 85 cd | Hannah 85 cd | Katharina 80 abc | Marie 75 bcd | Sophie 70 bcd | Michelle 60 ab | Vanessa 65 ab
  Jessica 65 ab | Jana 60 abc | Jennifer 60 a | Mia 65 cd | Lara 60 bcd | Emma 60 cd | Sophia 60 cd
  Nadine 55 a | Sandra 55 a | Stefanie 55 a | Christina 55 a | Nicole 55 a | Johanna 55 bcd | Alina 55 bcd
  Melanie 50 a | Sabrina 50 a | Nina 50 abc | Franziska 50 ab | Jasmin 50 ab | Emily 50 cd | Kathrin 40 a
  Anja 35 a | Tanja 35 a | Daniela 40 a | Claudia 25 a | Susanne 20 a | Andrea 25 a | Kristin 25 a
  Celine 45 bc | Chantal 25 ab | Jacqueline 25 ab | Janina 40 ab | Annika 45 ab | Carina 35 ab | Svenja 35 ab
  Sina 35 abc | Theresa 40 abc | Vivien 30 b | Selina 45 bc | Pia 40 bcd | Clara 45 cd | Charlotte 40 cd
  Emilia 45 d | Amelie 45 cd | Paula 40 cd | Luisa 45 cd | Louisa 30 cd | Lina 45 cd | Lilly 35 cd
  Maja 35 cd | Marlene 25 cd | Greta 25 d | Ida 15 d | Frieda 15 d | Mila 20 d | Ella 20 d
  Helena 25 cd | Antonia 30 cd | Victoria 30 bc | Viktoria 25 bc | Leni 20 d | Nele 35 cd | Finja 25 cd
  Merle 25 cd | Jule 25 cd | Josephine 30 bc | Lucy 15 cd | Zoe 25 cd | Chiara 30 cd | Elena 35 bcd
  Larissa 35 bc | Melina 30 cd | Annalena 25 bc | Kim 30 bc | Saskia 30 ab | Tamara 30 ab | Denise 25 a
  Yvonne 20 a | Manuela 15 a | Simone 25 a | Katja 30 a | Silke 15 a | Petra 10 a | Martina 15 a
  Birgit 10 a | Kerstin 15 a | Ramona 20 a | Diana 25 a | Bianca 25 a | Nathalie 30 ab | Natalie 35 ab
  Isabel 20 ab | Isabell 25 ab | Isabelle 25 b | Miriam 30 a | Rebecca 35 ab | Rebekka 20 ab | Ricarda 15 ab
  Verena 30 a | Eva 30 abc | Judith 20 a | Ann-Kathrin 30 ab | Anne 35 a | Maria 45 abc | Mareike 20 ab
  Marina 30 a | Madeleine 20 b | Linda 30 ab | Luise 15 cd | Ronja 20 cd | Fabienne 30 b | Joana 20 bc
  Hanna 45 cd | Angelina 30 bc | Lucie 15 cd | Milena 30 bcd | Romy 15 cd | Lotta 20 d | Carla 25 cd
  Carolin 35 ab | Caroline 30 ab | Katrin 30 a | Inga 15 a | Wiebke 15 ab | Imke 10 a | Jenny 35 ab
  Steffi 20 a | Lisa-Marie 30 bc | Anna-Lena 30 bc | Mara 25 bcd | Smilla 10 d | Tabea 15 bc | Kira 20 bc
  Alicia 30 bc | Alisa 15 cd | Angela 15 a | Monika 10 a | Sonja 25 a | Silvia 15 a | Ina 15 a
  Corinna 20 a | Doreen 15 a | Peggy 10 a | Mandy 20 a | Cindy 15 a | Nancy 15 a | Janine 35 ab
  Josefine 20 bc | Antje 10 a | Dana 20 ab | Tina 25 a | Nadja 25 a | Leoni 30 cd | Lea-Sophie 20 cd
  Noemi 15 cd | Fiona 25 bc | Amy 20 cd | Elisa 20 cd | Jil 15 cd | Joy 10 cd | Lucia 15 cd
  Svea 10 d | Thea 10 d | Lene 10 d | Mathilda 10 d | Malin 15 cd | Emely 15 cd | Emilie 15 cd
  Lia 15 d | Liv 10 d | Nora 20 cd | Mira 20 cd | Aaliyah 10 d | Lorena 15 bc | Sabine 20 a
  Anke 10 a | Ines 20 a | Bettina 10 a | Britta 10 a | Alena 15 bc | Aileen 15 bc | Jolina 15 cd
  Joline 15 cd | Shirin 15 bc | Sharon 10 b | Kimberly 15 bc | Stephanie 30 a | Melissa 35 ab | Nelly 10 d
  Vera 10 a | Elisabeth 15 ab | Magdalena 20 ab | Rosa 10 d | Stella 15 cd | Valerie 20 bc | Vivienne 10 b
  Lynn 15 cd | Romina 15 bc | Meike 15 a | Maike 15 ab | Cheyenne 10 bc | Annabell 10 c | Eileen 15 bc
  Laila 10 cd | Jasmina 10 ab | Nicola 10 a | Sara 40 bcd | Kristina 30 ab | Alexandra 40 ab | Anastasia 30 bc
  Ann-Christin 15 ab | Hannah-Sophie 10 d | Marie-Sophie 10 cd | Leana 10 d | Lenja 10 d | Juliane 20 a | Cora 10 d
  Elif 45 bcd | Merve 40 bc | Aylin 40 bcd | Selin 35 cd | Zeynep 40 bcd | Büşra 30 bc | Esra 35 ab
  Melek 25 bc | Dilara 30 bcd | Ecem 15 cd | Ayşe 30 ab | Fatma 25 a | Emine 20 a | Hatice 15 a
  Sevgi 10 a | Derya 25 a | Özlem 25 a | Sibel 20 a | Seda 25 ab | Gizem 25 bc | Tuğba 25 ab
  Kübra 25 bc | Hilal 20 bc | Sena 25 cd | Ceren 20 bc | Damla 20 bc | Nur 20 bc | Rabia 20 bc
  Beyza 20 cd | Elanur 15 cd | Yaren 15 cd | İlayda 15 cd | Dilan 20 bc | Hande 10 ab | Songül 10 a
  Leyla 25 bcd | Yasemin 30 ab | Sevda 15 a | Nesrin 10 a | Meryem 25 bcd | Esma 15 bc | Ebru 20 a
  Pınar 15 a | Aleyna 25 cd | Azra 20 d | Nisa 20 cd | Ela 15 d | Berfin 15 cd | Gülay 10 a
  Canan 15 a | Funda 10 a | Burcu 15 ab | Serap 10 a | Tülin 10 a | Deniz 15 bc | Şeyma 20 bc
  Fatima 30 bcd | Amina 25 cd | Aya 20 d | Mariam 20 cd | Maryam 15 cd | Nour 15 cd | Yasmin 25 bc
  Samira 20 bc | Leila 15 bc | Rania 15 cd | Malak 10 d | Hiba 10 cd | Salma 10 cd | Dunja 15 ab
  Ana 25 bc | Ivana 20 ab | Jelena 20 ab | Marija 15 ab | Milica 10 bc | Natalia 25 bc | Natascha 25 a
  Olga 15 a | Tatjana 20 a | Swetlana 10 a | Irina 15 a | Valentina 20 bc | Katarina 15 ab | Darja 15 bc
  Dominika 10 b | Agnieszka 10 a | Weronika 10 c | Paulina 25 bc | Patricia 20 ab | Gina 20 bc | Giulia 20 cd
  Alessia 15 cd | Francesca 10 bc | Sofia 25 cd | Ioana 10 bc | Lejla 15 bc | Amra 10 b | Emina 10 bc
  Albina 10 b | Lana 15 cd | Nika 10 d | Tijana 10 bc | Sanja 15 a | Dragana 10 a | Vesna 10 a
  Ksenia 10 bc | Polina 10 cd | Alina-Sophie 10 d | Elina 15 cd | Anja-Marie 5 b | Daria 10 bc | Arta 10 bc
`);

export const DE_MALE: WeightedName[] = names(`
  Lukas 90 bcd | Daniel 85 a | Alexander 80 abc | Jonas 80 bcd | Leon 80 bcd | Christian 75 a | Sebastian 75 ab
  Jan 75 abc | Tim 75 abcd | Maximilian 75 bcd | Tobias 70 ab | Philipp 70 abc | Florian 70 ab | Niklas 70 bcd
  Luca 70 cd | Finn 70 cd | Kevin 65 ab | Paul 65 bcd | Felix 65 bcd | Julian 65 bcd | Michael 60 a
  Patrick 60 ab | Dennis 60 ab | Marcel 60 ab | Fabian 60 abc | David 60 abcd | Dominik 55 ab | Benjamin 55 ab
  Ben 55 cd | Simon 55 abc | Johannes 55 abc | Stefan 50 a | Matthias 50 a | Pascal 50 ab | Moritz 50 bcd
  Elias 50 cd | Jannik 50 bc | Nico 50 bc | Marvin 50 ab | Thomas 40 a | Andreas 45 a | Markus 45 a
  Martin 45 a | Robin 45 ab | Nils 45 abc | Marco 45 ab | Manuel 45 ab | Justin 45 bc | Max 40 bcd
  Noah 40 d | Kai 40 ab | Oliver 40 a | Erik 40 bc | Jakob 40 cd | Luis 40 cd | Timo 40 ab
  Tom 40 bcd | Christoph 40 a | Lucas 40 bcd | Yannick 35 bc | Sven 35 a | Lennart 35 bcd | Adrian 35 bc
  Marius 35 bc | Jens 25 a | Lars 30 a | Steffen 30 a | Björn 30 a | Mario 30 a | René 30 a
  Sascha 30 a | Hendrik 30 ab | Joshua 30 bcd | Jonathan 30 bcd | Anton 30 d | Vincent 30 cd | Leo 30 d
  Samuel 30 cd | Aaron 30 cd | Nicolas 30 bc | Julius 30 cd | Marc 30 a | Luka 30 cd | Carsten 15 a
  Torsten 15 a | Dirk 10 a | Ronny 15 a | Rico 15 a | Danny 20 a | Mike 20 a | Leonard 25 cd
  Eric 25 bc | Henrik 25 cd | Jacob 15 cd | Jason 20 bc | Noel 20 cd | Lenny 15 d | Louis 25 cd
  Emil 25 d | Oskar 20 d | Theo 15 d | Henry 15 d | Mats 20 cd | Malte 25 bc | Ole 25 cd
  Hannes 25 bc | Till 25 bc | Torben 20 a | Jannis 25 bcd | Janis 20 bc | Tristan 15 cd | Valentin 25 cd
  Constantin 20 cd | Konstantin 25 bc | Linus 25 cd | Levin 20 cd | Lion 15 cd | Marlon 20 cd | Mika 20 d
  Mattis 15 d | Jonte 10 d | Bennet 15 cd | Benedikt 25 ab | Raphael 25 abc | Silas 15 d | Jeremy 15 bc
  Dustin 20 b | Sandro 15 a | Steven 15 a | Ricardo 15 ab | Mirko 15 a | Ingo 10 a | Holger 10 a
  Frank 15 a | Jörg 10 a | Heiko 10 a | Mark 15 a | Kilian 25 bc | Arne 20 a | Gregor 10 a
  Hauke 10 ab | Jonah 15 d | Jona 20 cd | Joel 25 cd | Lasse 20 cd | Laurin 15 cd | Leopold 10 d
  Magnus 10 d | Milan 25 cd | Nikolas 20 bc | Niclas 20 bc | Pierre 10 ab | Ruben 15 cd | Sören 20 ab
  Thilo 15 ab | Timon 15 cd | Yannik 15 bc | Fynn 25 d | Phil 15 bc | Philip 20 bc | Mathis 15 d
  Matteo 15 d | Lorenz 15 cd | Ludwig 10 d | Carl 15 cd | Karl 15 d | Gabriel 20 cd | Severin 10 bc
  Dario 20 bc | Denis 30 ab | Jannes 15 cd | Johann 15 cd | Maurice 20 b | Tim-Luca 5 cd | Jean 10 ab
  Mehmet 40 abc | Ali 40 abcd | Ahmet 35 abc | Mustafa 35 abc | Murat 40 a | Emre 45 bc | Can 40 bcd
  Burak 45 bc | Deniz 35 bcd | Hakan 30 a | Serkan 25 a | Kemal 15 a | Hüseyin 30 abc | Hasan 30 abc
  İbrahim 30 abcd | Ömer 30 bcd | Yusuf 30 cd | Furkan 35 bc | Onur 25 ab | Volkan 20 a | Erkan 20 a
  Ercan 15 a | Okan 15 a | Tolga 20 ab | Tayfun 10 a | Cem 25 ab | Kaan 30 bc | Enes 30 bc
  Berkay 25 bc | Berk 20 bc | Batuhan 20 bc | Arda 25 cd | Efe 20 cd | Eren 30 bcd | Yasin 25 bc
  Yunus 20 bc | Umut 25 bc | Oğuz 15 ab | Selim 20 ab | Sinan 20 a | Tarık 15 ab | Fatih 30 ab
  İsmail 20 ab | Kerem 25 bc | Mert 30 bc | Ozan 20 ab | Cihan 20 ab | Gökhan 20 a | Erdem 15 ab
  Tuncay 10 a | Levent 10 a | Halil 20 ab | Abdullah 20 abc | Emirhan 20 cd | Emir 25 cd | Yiğit 15 cd
  Alperen 15 cd | Baran 20 cd | Taha 15 cd | Kadir 15 a | Serdar 15 a | Sercan 10 a | Musa 15 bc
  Mohammed 30 bcd | Mohamed 25 bcd | Muhammed 30 bcd | Omar 25 bcd | Karim 20 bc | Amir 25 cd | Bilal 25 bc
  Hamza 25 cd | Samir 15 ab | Tarek 15 ab | Youssef 15 cd | Rami 10 bc | Malik 20 cd | Adam 25 cd
  Ilias 15 cd | Ilyas 15 cd | Khaled 10 ab | Mahmoud 10 bc | Nikola 25 bc | Ivan 25 ab | Marko 25 ab
  Filip 25 bc | Petar 10 b | Dragan 10 a | Goran 10 a | Zoran 10 a | Dejan 15 a | Mateo 15 d
  Ante 10 ab | Josip 10 ab | Ivo 10 a | Edin 10 ab | Amar 15 bc | Adnan 15 ab | Armin 20 ab
  Mirza 10 b | Kenan 15 b | Damir 10 a | Haris 10 bc | Dino 15 bc | Arben 10 a | Blerim 10 a
  Dardan 10 bc | Arian 15 cd | Kristian 15 b | Dimitri 15 ab | Artur 20 ab | Arthur 20 d | Viktor 15 ab
  Waldemar 10 a | Eugen 15 a | Vitali 15 a | Sergej 15 a | Andrej 15 ab | Alexej 10 a | Maxim 20 bc
  Nikita 15 bc | Pawel 10 a | Piotr 10 a | Kacper 10 c | Jakub 15 bc | Łukasz 15 ab | Marcin 10 a
  Mateusz 15 bc | Tomasz 10 a | Krzysztof 10 a | Antonio 20 ab | Alessandro 15 bc | Giuseppe 10 a | Francesco 10 b
  Lorenzo 10 d | Angelo 10 a | Salvatore 10 a | Leonardo 15 d | Gianluca 10 b | Pedro 10 b | Miguel 10 b
  José 10 a | Carlos 10 a | Diego 10 c | Dimitrios 10 a | Georgios 10 ab | Nikolaos 10 ab | Christos 10 a
  Stefano 10 ab | Davide 10 bc | Emanuel 15 ab | Elia 10 d | Liam 15 d | Emilio 10 d | Leandro 10 cd
`);

export const DE_SURNAMES: Weighted[] = surnames(`
  Müller 100 | Schmidt 90 | Schneider 60 | Fischer 55 | Weber 50 | Meyer 50 | Wagner 48 | Becker 45 | Schulz 45
  Hoffmann 44 | Schäfer 40 | Koch 38 | Bauer 38 | Richter 37 | Klein 37 | Wolf 35 | Schröder 35 | Neumann 34
  Schwarz 34 | Zimmermann 33 | Braun 32 | Krüger 32 | Hofmann 31 | Hartmann 31 | Lange 30 | Schmitt 30 | Werner 30
  Schmitz 30 | Krause 29 | Meier 29 | Lehmann 28 | Schmid 28 | Schulze 27 | Maier 27 | Köhler 26 | Herrmann 26
  König 25 | Walter 25 | Mayer 25 | Huber 24 | Kaiser 24 | Fuchs 24 | Peters 23 | Lang 23 | Scholz 23
  Möller 22 | Weiß 22 | Jung 22 | Hahn 22 | Schubert 21 | Vogel 21 | Friedrich 21 | Keller 21 | Günther 20
  Frank 20 | Berger 20 | Winkler 19 | Roth 19 | Beck 19 | Lorenz 19 | Baumann 18 | Franke 18 | Albrecht 18
  Schuster 18 | Simon 18 | Ludwig 17 | Böhm 17 | Winter 17 | Kraus 17 | Martin 17 | Schumacher 16 | Krämer 16
  Vogt 16 | Stein 16 | Jäger 16 | Otto 16 | Sommer 15 | Groß 15 | Seidel 15 | Heinrich 15 | Brandt 15
  Haas 15 | Schreiber 15 | Graf 14 | Schulte 14 | Dietrich 14 | Ziegler 14 | Kuhn 14 | Kühn 14 | Pohl 14
  Engel 14 | Horn 13 | Busch 13 | Bergmann 13 | Thomas 13 | Voigt 13 | Sauer 13 | Arnold 13 | Wolff 13
  Pfeiffer 12 | Ernst 12 | Lindner 12 | Hübner 12 | Jansen 12 | Franz 12 | Hansen 12 | Peter 11 | Wenzel 11
  Götz 11 | Paul 11 | Ebert 11 | Kraft 11 | Barth 11 | Kroll 10 | Thiel 10 | Brinkmann 10 | Kruse 10
  Kretschmer 9 | Böttcher 9 | Fiedler 9 | Schütz 9 | Ritter 9 | Arndt 9 | Kirchner 9 | Reuter 9 | Beyer 9
  Steiner 9 | Wendt 8 | Stahl 8 | Behrens 8 | Schilling 8 | Sander 8 | Marx 8 | Michel 8 | Schröter 8
  Heinz 8 | Bock 8 | Hesse 8 | Seifert 8 | Petersen 8 | Ahrens 7 | Gerlach 7 | Kunz 7 | Wilhelm 7
  Rose 7 | Jakob 7 | Frey 7 | Moser 7 | Bachmann 7 | Lutz 7 | Zimmer 7 | Schramm 7 | Witt 7
  Ackermann 7 | Weis 6 | Hein 6 | Kurz 6 | Kühl 5 | Thieme 5 | Heller 6 | Brückner 5 | Hartung 5
  Yılmaz 30 | Kaya 22 | Demir 22 | Şahin 20 | Çelik 20 | Yıldız 18 | Öztürk 18 | Yıldırım 16 | Aydın 16
  Özdemir 16 | Arslan 15 | Doğan 15 | Kılıç 13 | Aslan 13 | Çetin 12 | Kara 12 | Koç 12 | Kurt 12
  Özkan 11 | Şimşek 11 | Polat 10 | Korkmaz 10 | Güneş 10 | Akın 9 | Aktaş 9 | Bulut 9 | Keskin 9
  Uçar 8 | Tekin 8 | Karaca 8 | Avcı 8 | Ateş 8 | Coşkun 8 | Turan 8 | Özer 8 | Yavuz 8
  Aksoy 8 | Altun 7 | Güler 7 | Acar 7 | Ünal 7 | Erdem 7 | Sarı 7 | Nowak 12 | Kowalski 9
  Lewandowski 7 | Wiśniewski 6 | Wójcik 6 | Kowalczyk 6 | Kamiński 6 | Zieliński 6 | Szymański 5 | Popović 7 | Jovanović 7
  Petrović 7 | Nikolić 6 | Marković 6 | Kovačević 6 | Horvat 6 | Babić 5 | Hodžić 5 | Hadžić 5 | Begić 5
  Krasniqi 6 | Berisha 6 | Gashi 5 | Hoxha 5 | Iwanow 5 | Petrow 5 | Rossi 6 | Russo 6 | Esposito 5
  Romano 5 | Bianchi 5 | Papadopoulos 6 | Georgiou 4 | Ahmad 7 | Ali 8 | Hussein 6 | Hassan 6 | Ibrahim 6
  Khalil 5 | Saleh 4 | Haddad 4 | Abdullah 4 | Nguyen 8 | Tran 5 | Morina 4 | Schneider-Yılmaz 2 | Wagner-Kaya 2
`);
